import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Calendar,
  Upload,
  X,
  ChevronLeft,
  Loader2,
  Save,
} from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { cn } from '@utils';
import api from '@services/api';
import type { Event } from '@types';

const eventSchema = z.object({
  name: z.string().min(3, 'Event name must be at least 3 characters'),
  description: z.string().optional(),
  type: z.enum(['single', 'recurring', 'multiday']),
  start_date: z.string().min(1, 'Start date is required'),
  end_date: z.string().optional(),
  capacity: z.number().min(1, 'Capacity must be at least 1'),
  currency: z.string().default('USD'),
  is_featured: z.boolean().default(false),
  is_private: z.boolean().default(false),
  status: z.enum(['draft', 'published', 'paused', 'cancelled', 'completed']).optional(),
  category: z.string().optional(),
  timezone: z.string().optional(),
});

type EventFormData = z.infer<typeof eventSchema>;

function toLocalDatetime(dateStr?: string): string {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr);
    const offset = d.getTimezoneOffset();
    const local = new Date(d.getTime() - offset * 60000);
    return local.toISOString().slice(0, 16);
  } catch {
    return '';
  }
}

export default function EventEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [featuredImage, setFeaturedImage] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['event', id],
    queryFn: () => api.get<{ data: Event }>(`/events/${id}`),
    enabled: !!id,
  });

  const event = data?.data;

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<EventFormData>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      type: 'single',
      currency: 'USD',
      capacity: 100,
      is_featured: false,
      is_private: false,
    },
  });

  useEffect(() => {
    if (event) {
      reset({
        name: event.name,
        description: event.description || '',
        type: event.type,
        start_date: toLocalDatetime(event.startDate),
        end_date: toLocalDatetime(event.endDate),
        capacity: event.capacity,
        currency: event.currency,
        is_featured: event.isFeatured,
        is_private: event.isPrivate,
        status: event.status,
        category: event.category || '',
        timezone: event.timezone || '',
      });
      setFeaturedImage(event.featuredImage || null);
    }
  }, [event, reset]);

  const onSubmit = async (data: EventFormData) => {
    setIsSubmitting(true);
    try {
      await api.put(`/events/${id}`, data);
      toast.success('Event updated successfully!');
      navigate(`/events/${id}`);
    } catch (error: any) {
      toast.error(error.message || 'Failed to update event');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setFeaturedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto space-y-6 animate-pulse">
        <div className="h-10 bg-gray-200 rounded w-48 dark:bg-gray-700" />
        <div className="h-96 bg-gray-200 rounded-xl dark:bg-gray-700" />
      </div>
    );
  }

  if (!event) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Event not found</h2>
        <Link to="/events" className="btn-primary mt-4 inline-flex">Back to Events</Link>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-4xl mx-auto space-y-6"
    >
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to={`/events/${id}`} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800">
            <ChevronLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Edit Event</h1>
            <p className="text-gray-500 dark:text-gray-400">{event.name}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Basic Info */}
        <div className="card p-6 space-y-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h2>

          {/* Featured Image */}
          <div>
            <label className="label">Featured Image</label>
            <div className="relative">
              {featuredImage ? (
                <div className="relative w-full h-48 rounded-lg overflow-hidden">
                  <img src={featuredImage} alt="Featured" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setFeaturedImage(null)}
                    className="absolute top-2 right-2 p-2 bg-white rounded-lg shadow-lg hover:bg-gray-100"
                    aria-label="Remove Image"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-48 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-primary-500 dark:border-gray-600 dark:hover:border-primary-400">
                  <Upload className="w-8 h-8 text-gray-400 mb-2" />
                  <span className="text-sm text-gray-500">Click to upload image</span>
                  <input type="file" accept="image/*" className="hidden" onChange={handleImageUpload} />
                </label>
              )}
            </div>
          </div>

          {/* Name */}
          <div>
            <label htmlFor="name" className="label">Event Name *</label>
            <input
              {...register('name')}
              type="text"
              id="name"
              className={cn('input', errors.name && 'input-error')}
            />
            {errors.name && <p className="mt-1 text-sm text-danger-600">{errors.name.message}</p>}
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="label">Description</label>
            <textarea
              {...register('description')}
              id="description"
              rows={4}
              className="input"
            />
          </div>

          {/* Status */}
          <div>
            <label htmlFor="status" className="label">Status</label>
            <select {...register('status')} id="status" className="input">
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="paused">Paused</option>
              <option value="cancelled">Cancelled</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          {/* Event Type */}
          <div>
            <label className="label">Event Type</label>
            <div className="grid grid-cols-3 gap-4">
              {['single', 'recurring', 'multiday'].map((type) => (
                <label
                  key={type}
                  className={cn(
                    'flex flex-col items-center p-4 border-2 rounded-lg cursor-pointer transition-colors',
                    watch('type') === type
                      ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                      : 'border-gray-200 hover:border-gray-300 dark:border-gray-700'
                  )}
                >
                  <input {...register('type')} type="radio" value={type} className="sr-only" />
                  <Calendar className="w-6 h-6 mb-2 text-gray-500" />
                  <span className="text-sm font-medium capitalize">{type}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Date & Time */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="start_date" className="label">Start Date *</label>
              <input
                {...register('start_date')}
                type="datetime-local"
                id="start_date"
                className={cn('input', errors.start_date && 'input-error')}
              />
              {errors.start_date && <p className="mt-1 text-sm text-danger-600">{errors.start_date.message}</p>}
            </div>
            <div>
              <label htmlFor="end_date" className="label">End Date</label>
              <input {...register('end_date')} type="datetime-local" id="end_date" className="input" />
            </div>
          </div>

          {/* Capacity & Currency */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="capacity" className="label">Capacity *</label>
              <input
                {...register('capacity', { valueAsNumber: true })}
                type="number"
                id="capacity"
                min={1}
                className={cn('input', errors.capacity && 'input-error')}
              />
              {errors.capacity && <p className="mt-1 text-sm text-danger-600">{errors.capacity.message}</p>}
            </div>
            <div>
              <label htmlFor="currency" className="label">Currency</label>
              <select {...register('currency')} id="currency" className="input">
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="QAR">QAR</option>
                <option value="SAR">SAR</option>
                <option value="AED">AED</option>
              </select>
            </div>
          </div>

          {/* Toggles */}
          <div className="flex items-center gap-6">
            <label className="flex items-center gap-2 cursor-pointer">
              <input {...register('is_featured')} type="checkbox" className="rounded text-primary-600" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Featured Event</span>
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input {...register('is_private')} type="checkbox" className="rounded text-primary-600" />
              <span className="text-sm text-gray-700 dark:text-gray-300">Private Event</span>
            </label>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-between">
          <Link to={`/events/${id}`} className="btn-secondary">Cancel</Link>
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Changes
              </>
            )}
          </button>
        </div>
      </form>
    </motion.div>
  );
}
